import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Stack } from "react-bootstrap";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { deleteDwelling, patchDwelling } from "../../../services/httpClient";
import { unsavedChanges } from "../../../App";
import Input from "../../ui/Input";
import { propertyAddressLine } from "../../../helpers/housingAssignment";
import SelectObject from "../../ui/SelectObject";
import IconButton from "../../ui/IconButton";
import states from "../../../data/states.json";

interface State {
  key: string;
  name: string;
}

interface CardState {
  dwelling: any;
  dwellingTypes: any[];
  getItems: any;
  onManageRooms: any;
  setOrgToSave: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const DwellingManagementCard = ({
  dwelling,
  dwellingTypes,
  getItems,
  onManageRooms,
  setOrgToSave,
  setErrMessage,
  setShowToast,
  setToastText,
  setToastBody,
}: CardState) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [itemName, setItemName] = useState<string>("");
  const [itemTypeId, setItemTypeId] = useState<string>("");
  const [itemAddress1, setItemAddress1] = useState<string>("");
  const [itemCity, setItemCity] = useState<string>("");
  const [itemState, setItemState] = useState<string>("");
  const [itemZip, setItemZip] = useState<string>("");
  const statesList: State[] = states;

  useEffect(() => {
    if (dwelling) {
      setItemName(dwelling?.name);
      setItemTypeId(dwelling?.dwelling_type_id);
      setItemAddress1(dwelling?.address_line_one);
      setItemCity(dwelling?.city);
      setItemState(dwelling?.state);
      setItemZip(dwelling?.zip);
    }
  }, [dwelling]);

  const onClickEdit = (e: any) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const onClickManageRooms = (e: any) => {
    e.stopPropagation();
    onManageRooms(dwelling);
  };

  const onClickEditFinal = () => {
    if (!itemName) {
      setErrMessage("Cannot set null dwelling name");
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }

    const updatedDwelling = {
      name: itemName,
      dwelling_type_id: itemTypeId,
      address_line_one: itemAddress1,
      city: itemCity,
      state: itemState,
      zip: itemZip,
    };

    setErrMessage("");

    patchDwelling(updatedDwelling, dwelling?.id)
      .then((res: any) => {
        unsavedChanges.value = false;
        setOrgToSave(false);
        setShowToast(true);
        setToastText("Dwelling Updated");
        setToastBody(itemName);
        setIsEditing(false);
        getItems();
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  const onClickCancelEdit = () => {
    unsavedChanges.value = false;
    setOrgToSave(false);
    setIsEditing(false);
    setErrMessage(undefined);
  };

  const markUnsaved = () => {
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMessage(undefined);
  };

  const onUpdateName = (e: any) => {
    setItemName(e.target.value);
    markUnsaved();
  };
  const onUpdateType = (e: any) => {
    setItemTypeId(e.target.value);
    markUnsaved();
  };
  const onUpdateAddress1 = (e: any) => {
    setItemAddress1(e.target.value);
    markUnsaved();
  };
  const onUpdateCity = (e: any) => {
    setItemCity(e.target.value);
    markUnsaved();
  };
  const onUpdateState = (e: any) => {
    setItemState(e.target.value);
    markUnsaved();
  };
  const onUpdateZip = (e: any) => {
    setItemZip(e.target.value);
    markUnsaved();
  };

  const onClickDelete = (e: any) => {
    e.stopPropagation();
    setIsDeleting(true);
  };

  const onClickDeleteFinal = () => {
    setErrMessage("");
    deleteDwelling(dwelling?.id)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Dwelling Deleted");
        setToastBody(dwelling?.name);
        setIsDeleting(false);
        getItems();
      })
      .catch((err: any) => {
        setIsDeleting(false);
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({ top: 0, behavior: "auto" });
      });
  };

  const onClickCancelDelete = () => {
    setErrMessage("");
    setIsDeleting(false);
    setErrMessage(undefined);
  };

  const summaryLine = () => {
    const parts = [propertyAddressLine(dwelling)].filter(Boolean);
    parts.push(`${dwelling?.room_count ?? 0} rooms`, `${dwelling?.bed_count ?? 0} beds`);
    return parts.join(" - ");
  };

  const renderEdit = () => {
    return (
      <Card key={dwelling.name} className="bg-light mb-1">
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Input label="Name" type="text" value={itemName} onUpdate={(e: any) => onUpdateName(e)} required={true} />
            </Col>
            <Col xs={12} md={6}>
              <SelectObject label="Type" value={itemTypeId} options={dwellingTypes} onUpdate={(e: any) => onUpdateType(e)} />
            </Col>
            <Col xs={12} md={6}>
              <Input label="Address" type="text" value={itemAddress1} onUpdate={(e: any) => onUpdateAddress1(e)} />
            </Col>
            <Col xs={12} md={3}>
              <Input label="City" type="text" value={itemCity} onUpdate={(e: any) => onUpdateCity(e)} />
            </Col>
            <Col xs={6} md={3}>
              <SelectObject label="State" value={itemState} options={statesList} onUpdate={(e: any) => onUpdateState(e)} />
            </Col>
            <Col xs={6} md={3}>
              <Input label="Zip" type="text" value={itemZip} onUpdate={(e: any) => onUpdateZip(e)} />
            </Col>
          </Row>
          <Stack direction="horizontal" gap={3} className="mt-3">
            <Button className="app-secondary-color button" onClick={onClickEditFinal}>
              Save
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={onClickCancelEdit}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderDelete = () => {
    return (
      <Card key={dwelling.name} className="bg-light mb-1">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} gap={2}>
              <Card.Subtitle>Are you sure you want to delete {dwelling?.name} and its rooms?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={onClickDeleteFinal}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={onClickCancelDelete}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  return (
    <>
      {isEditing ? (
        <>{renderEdit()}</>
      ) : isDeleting ? (
        <>{renderDelete()}</>
      ) : (
        <Card className="bg-light mb-0">
          <Card.Body className="p-2">
            <Stack direction="horizontal" gap={3}>
              <Stack gap={1} className="align-self-center">
                <div className="small">{`${dwelling?.name} ${dwelling?.dwelling_type_name ? `- ${dwelling?.dwelling_type_name}` : ""}`}</div>
                {summaryLine() && <div className="small text-muted">{summaryLine()}</div>}
              </Stack>

              <Button className="app-primary-color-inverse button" onClick={(e) => onClickManageRooms(e)}>
                Manage Rooms
              </Button>
              <div className="vr" />
              <IconButton onClickButton={onClickEdit} tooltipText={"Edit"} icon={faEdit} />
              <div className="vr" />
              <IconButton onClickButton={onClickDelete} tooltipText={"Delete"} icon={faTrash} />
            </Stack>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default DwellingManagementCard;
